import { Temporal } from '@js-temporal/polyfill';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { debounceTime, Subject } from 'rxjs';
import { StoryItem } from './story-item';
import { ScriptStory } from './script-story';
import { ScriptOutput } from './script-output';
import { Notepad } from './notepad';
import { Project } from './project';
import { Script } from './script';
import { NotepadItem } from './notepad-item';
import { Img } from './img';

export class Story {
    id: string;
    title: string;
    description: string;

    created: Temporal.ZonedDateTime;

    private _currentItem?: StoryItem;
    private _editItem?: StoryItem;

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    private _itemLs: (StoryItem | Img)[] = [];

    constructor(
        public coords: CoordinatorService,
        public params: any,
    ) {
        this.id = params.id || this.coords.uniqueId();
        this.title = params.title || this.coords.text('newNotepad');
        this.description = params.description || '';
        this.created = params.created
            ? Temporal.ZonedDateTime.from(params.created)
            : this.coords.utils$.zonedDateTimeNow;

        this._markForManifestStore$.subscribe(() => this._manifestStore());

        params.itemLs?.forEach((x: any) => {
            let itm: StoryItem | Img;
            if (x.mimetype?.startsWith('image/'))
                itm = new Img(this.coords, Object.assign(x, { parent: this }));
            else itm = new StoryItem(this.coords, this, x);
            this._itemLs.push(itm);
        });
    }

    get notepadItemIdSet() {
        const s = new Set<string>();
        this._itemLs.forEach((ntpItm) => ntpItm.notepadItemIdLs.forEach((id) => s.add(id)));
        return s;
    }

    get itemLs() {
        if (!this._itemLs.length) this.addItem();
        return Array.from(this._itemLs);
    }

    activate() {
        this.coords.notepad$.current = this;
    }

    public get currentItem() {
        return this._currentItem;
    }
    public set currentItem(value) {
        this._currentItem = value;
        this.coords.display$.markForCheck();
    }

    public get editItem() {
        return this._editItem;
    }
    public set editItem(value) {
        this._editItem = value;
        this.coords.display$.markForCheck();
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    deleteItem(itm: StoryItem | Img) {
        const ls = this._itemLs;
        let i = ls.indexOf(itm);
        ls.splice(i, 1);
        this.markForManifestStore();
    }

    moveItem(partItm: StoryItem | Img, isUp: boolean) {
        const ls = this._itemLs;
        let i = ls.indexOf(partItm);
        if ((isUp && i < 1) || (!isUp && i === ls.length - 1)) return partItm;
        !isUp && i++;
        [ls[i], ls[i - 1]] = [ls[i - 1], ls[i]];
        this.coords.display$.markForCheck();
        this.markForManifestStore();
        return partItm;
    }

    addItem(partItm?: StoryItem | Img, isBefore = false, kind = 'story-item'): StoryItem | Img {
        let itm: StoryItem | Img;
        let i: number;
        if (partItm) {
            i = this._itemLs.indexOf(partItm);
            if (!isBefore) i++;
        }

        switch (kind) {
            case 'story-item':
                itm = new StoryItem(this.coords, this, {
                    parentStoryId: this.id,
                });
                itm.isEditMode = true;
                break;
            case 'image':
                itm = new Img(this.coords, {
                    parent: this,
                });
                itm.edit({ isNew: true, position: i! });
                return itm;
        }
        if (!itm!) return itm!;

        this.finalizeAddItem(itm, i!);
        return itm!;
    }

    finalizeAddItem(itm: StoryItem | Img, position?: number) {
        if (position === undefined) position = this._itemLs.push(itm);
        else this._itemLs.splice(position, 0, itm);
        this.coords.display$.markForCheck();
        this.markForManifestStore();
        return itm!;
    }

    addItemFromNotepadItem(notepadItem: NotepadItem): StoryItem {
        const itm = new StoryItem(this.coords, this, {
            parentStoryId: this.id,
        });
        this._itemLs.push(itm);
        itm.body = notepadItem.body;
        itm.setNotepadItem(notepadItem, true);
        this.markForManifestStore();
        return itm;
    }

    private _manifestDelete() {
        const ls = [
            {
                id: this.id,
                type: 'notepad',
            },
        ];
        this.coords.manifest$.delete(ls);
    }

    get(id: string) {
        for (const itm of this._itemLs) if (itm.id === id) return itm;
        return undefined;
    }

    delete() {
        this.coords.notepad$.delete(this);
        this._manifestDelete();
        this.coords.display$.markForCheck();
    }

    private _manifestStore() {
        this.coords.manifest$.store({
            type: 'notepad',
            id: this.id,
            valueMap: new Map<string, any>([
                ['isStory', true],
                ['title', this.title],
                ['description', this.description],
                ['itemLs', this.itemLs.map((itm) => itm.save())],
            ]),
        });
    }

    json() {
        return {
            id: this.id,
            title: this.title,
            description: this.description,
            created: this.created.toJSON(),
            itemLs: this.itemLs.map((itm) => itm.save()),
        };
    }

    get scriptClass() {
        return new ScriptStory(this.coords, this);
    }

    async hubExport(params?: any) {
        const out = new ScriptOutput(this.coords, this.title);
        out.title = this.title;

        const pr = new Project(this.coords, { title: this.title });
        out.addProject(pr.scriptClass);

        out.addStories(this.scriptClass);

        const notepadItemLs = this.coords.notepad$.notepadItemsFromId(
            Array.from(this.notepadItemIdSet),
        );

        const ntp = new Notepad(this.coords, { title: this.coords.text('Notes') });

        notepadItemLs.forEach((origItm) => {
            const itm = ntp.addItem();
            itm.id = origItm.id;
            itm.title = origItm.title;
            itm.body = origItm.body;
            itm.evidence = origItm.evidence;
        });

        out.addNotepads(ntp.scriptClass);

        const startScript = new Script(this.coords, {
            id: '_storyStartScript',
            source: `const s = $project.stories[0];s && s.display()`,
            title: this.coords.text('Start'),
            onStart: true,
        });

        out.addScripts(startScript);

        if (params?.isLocalSave) {
            await out.save();
            return;
        }

        const payload = await out.save({ returnBlob: true });

        return payload;
    }
}
